import { Injectable } from '@angular/core';
import {
  startOfDay,
  parseISO,
  format,
  parse,
  subHours,
  differenceInMinutes,
  differenceInHours,
} from 'date-fns';
import { TZDate } from '@date-fns/tz';
import moment from 'moment-timezone';
import { Moment } from 'moment';
import { ptBR } from 'date-fns/locale';

@Injectable({
  providedIn: 'root',
})
export class DateUtilsService {
  constructor() {}

  formatDateToISO(value: any) {
    if (value) {
      value = value.substring(0, 10);
      const date = parse(value, 'dd/MM/yyyy', new Date());
      return date.toJSON();
    }
    return value;
  }

  convertDateStringFormat(
    dateString: string,
    fromFormat: string = 'DD/MM/YYYY',
    toFormat: string = 'YYYY-MM-DD',
  ): string {
    if (!dateString) return '';
    const date = moment(dateString, fromFormat);
    if (!date.isValid()) return '';
    return date.format(toFormat);
  }

  formatStringToISO8601(value: string) {
    if (!value) {
      return '';
    }
    value = value.substring(0, 10);
    const date = parse(value, 'dd/MM/yyyy', new Date());
    const dateMoment = moment(date);
    return this.fromMomentObjToISO8601(dateMoment);
  }

  formatISOToDate(isoDate: string, formatString: string = 'dd/MM/yyyy') {
    if (!isoDate) {
      return '';
    }
    const date = parseISO(isoDate);
    return format(date, formatString);
  }

  formatISOToDateReadable(isoDate: string) {
    if (!isoDate) {
      return '';
    }

    const formatted = moment
      .utc(isoDate)
      .tz('America/Sao_Paulo')
      .format('DD/MM/YYYY [às] HH:mm');

    return formatted;
  }

  formatISODateTimeToBr(isoDateTime: string) {
    if (!isoDateTime) {
      return '';
    }

    return moment(isoDateTime).format('DD/MM/YYYY HH:mm');
  }

  formatDateToReadable(dateString: string) {
    const cleanDateString = dateString.split('[')[0];

    const date = new Date(cleanDateString);

    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'America/Sao_Paulo',
    };

    return new Intl.DateTimeFormat('pt-BR', options).format(date);
  }

  isValidDateString(dateStr: string): boolean {
    //If user empties the input
    if (dateStr === '' || dateStr === null) {
      return true;
    }

    // Ensure the date string has a valid year (at least 4 digits)
    const yearMatch = dateStr.match(/(\d{4})/);
    if (!yearMatch) {
      return false;
    }

    // Check with Date
    const dateObj = new Date(dateStr);
    if (!isNaN(dateObj.getTime())) {
      return true;
    }

    // Check with moment
    const momentObj = moment(dateStr, moment.ISO_8601, true);
    return momentObj.isValid();
  }

  formatDateToISO8601(date: Date | Moment) {
    if (!date) {
      return '';
    }

    if (date instanceof Date) {
      return this.fromDateObjToISO8601(date);
    }

    if (moment.isMoment(date)) {
      return this.fromMomentObjToISO8601(date);
    }

    return date;
  }

  formatDateToYearOnly(date: Moment | string) {
    if (!date) {
      return '';
    }

    const momentDate =
      typeof date === 'string' ? moment(date, 'YYYY-MM-DD') : date;
    return momentDate.year().toString();
  }

  formatDateToTemplate(date: Date | Moment | string): string {
    if (!date) return '';

    let momentDate: Moment;

    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      momentDate = moment(date, 'YYYY-MM-DD');
    } else {
      momentDate = moment(date);
    }

    return momentDate.utc().format('DD/MM/YYYY');
  }

  formatHourToLocaltime(hour: string) {
    if (!hour || hour === '') {
      return '';
    }
    return `${hour}:00`;
  }

  shiftUtcISOTimezone(
    isoString: string | null,
    timeZone = 'America/Sao_Paulo',
  ): string {
    if (!isoString) {
      return '';
    }
    const newDateOjb = new TZDate(isoString, timeZone);
    return newDateOjb.toISOString();
  }

  isDayPassed(date: Date) {
    const today = new Date();

    return startOfDay(date) < startOfDay(today);
  }

  isSameDay(date1: Date) {
    const today = new Date();

    return (
      date1.getFullYear() === today.getFullYear() &&
      date1.getMonth() === today.getMonth() &&
      date1.getDate() === today.getDate()
    );
  }

  isTimePassed(time: string) {
    const [hh, mm] = time.split(':').map(Number);
    const now = new Date();
    const timeDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      hh,
      mm,
    );

    return timeDate < now;
  }

  formatNotificationDate(dateStr: string): string {
    if (!dateStr) return '';

    const zonedDate = moment.utc(dateStr).tz('America/Sao_Paulo').toDate();
    const now = new Date();

    const diffMinutes = differenceInMinutes(now, zonedDate);
    const diffHours = differenceInHours(now, zonedDate);

    if (diffMinutes < 60) {
      return `Há ${diffMinutes} minuto${diffMinutes === 1 ? '' : 's'}.`;
    }

    if (diffHours < 24) {
      return `Há ${diffHours} hora${diffHours === 1 ? '' : 's'}.`;
    }

    return format(zonedDate, "EEEE dd/MM/yyyy - 'às' HH:mm'h.'", {
      locale: ptBR,
    });
  }

  swapDateFormat(
    dateString: string | moment.Moment | Date,
    formatFrom: string,
    formatTo: string,
  ) {
    if (!dateString || dateString === '') {
      return '';
    }

    if (moment.isMoment(dateString)) {
      return dateString.format(formatTo);
    }

    if (typeof dateString === 'string') {
      const formatted = moment.utc(dateString).format(formatTo);

      return formatted;
    }

    if (dateString instanceof Date) {
      return moment(dateString).format(formatTo);
    }

    return '';
  }

  isDateSameOrBefore(date: any, dateToCompare: any) {
    date = this.safeConvertInputToMoment(date);

    if (!dateToCompare) {
      dateToCompare = moment().startOf('day');
    } else {
      dateToCompare = this.safeConvertInputToMoment(dateToCompare);
    }

    if (!date.isValid() || !dateToCompare.isValid()) {
      return false;
    }

    return date.isSameOrBefore(dateToCompare, 'day');
  }

  isDateSameOrAfter(date: any, dateToCompare: any) {
    date = this.safeConvertInputToMoment(date);

    if (!dateToCompare) {
      dateToCompare = moment().startOf('day');
    } else {
      dateToCompare = this.safeConvertInputToMoment(dateToCompare);
    }

    if (!date.isValid() || !dateToCompare.isValid()) {
      return false;
    }

    return date.isSameOrAfter(dateToCompare, 'day');
  }

  isDateBefore(date: any, dateToCompare: any) {
    date = this.safeConvertInputToMoment(date);

    if (!dateToCompare) {
      dateToCompare = moment().startOf('day');
    } else {
      dateToCompare = this.safeConvertInputToMoment(dateToCompare);
    }

    if (!date.isValid() || !dateToCompare.isValid()) {
      return false;
    }

    return date.isBefore(dateToCompare, 'day');
  }

  isDateAfter(date: any, dateToCompare: any) {
    date = this.safeConvertInputToMoment(date);

    if (!dateToCompare) {
      dateToCompare = moment().startOf('day');
    } else {
      dateToCompare = this.safeConvertInputToMoment(dateToCompare);
    }

    if (!date.isValid() || !dateToCompare.isValid()) {
      return false;
    }

    return date.isAfter(dateToCompare, 'day');
  }

  private safeConvertInputToMoment(dateValue: any) {
    if (typeof dateValue === 'string') {
      return moment(dateValue, 'YYYY-MM-DD');
    } else {
      return moment(dateValue);
    }
  }

  private fromDateObjToISO8601(date: Date) {
    const dateMoment = moment(date);
    return this.fromMomentObjToISO8601(dateMoment);
  }

  private fromMomentObjToISO8601(date: Moment) {
    date = date.utc();
    const day = date.date().toString().padStart(2, '0');
    const month = (date.month() + 1).toString().padStart(2, '0');
    const year = date.year().toString();
    return `${year}-${month}-${day}`;
  }

  private formatDateObjToISO(date: Date) {
    if (!date) {
      return '';
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  getDataAtual(formatString: string = 'yyyy-MM-dd') {
    const date = new Date();
    return format(date, formatString);
  }
}
